import type { ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import {
  DetailList,
  type DetailListRow,
  DetailListSkeleton,
} from '@/components/custom/detail-list';
import { KindBadge } from '@/components/custom/kind-filter';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, serviceRolesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/service-roles/$id/')({
  validateSearch: detailSearch,
  component: ServiceRoleDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';

function ServiceRoleDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('service_role.view', 'service roles');
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.serviceRoles.detail(id),
    queryFn: () => serviceRolesApi.get(id),
    enabled: canView,
  });

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'service_roles',
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({
        entityType: 'service_roles',
        entityId: id,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canView && canViewAudit,
  });

  const role = detail.data?.data ?? null;
  const [editOpen, setEditOpen] = useState(false);

  const statusMutation = useMutation({
    mutationFn: (target: 'activate' | 'deactivate') =>
      target === 'activate' ? serviceRolesApi.restore(id) : serviceRolesApi.remove(id),
    onSuccess: (_, target) => {
      toast.success(target === 'activate' ? 'Reactivated' : 'Deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceRoles.all });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Status change failed';
      toast.error(msg);
    },
  });

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/service-roles">Back to service roles</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : role ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{role.name}</h1>
                <StatusBadge active={role.active} />
              </div>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <code className="rounded bg-muted px-1 py-0.5 text-xs" title="Short code">
                  {role.shortCode}
                </code>
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RequirePermission permission="service_role.edit">
            {role?.active ? (
              <Button
                variant="outline"
                disabled={statusMutation.isPending}
                onClick={() => {
                  if (confirm(`Deactivate "${role.name}"?`)) {
                    statusMutation.mutate('deactivate');
                  }
                }}
              >
                Deactivate
              </Button>
            ) : role ? (
              <Button
                variant="outline"
                disabled={statusMutation.isPending}
                onClick={() => statusMutation.mutate('activate')}
              >
                Reactivate
              </Button>
            ) : null}
            <Button disabled={!role} onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
          </RequirePermission>
        </div>
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="surface-card rounded-md border">
            {detail.isLoading ? (
              <DetailListSkeleton rows={DETAIL_ROW_COUNT} />
            ) : role ? (
              <DetailsTable role={role} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Service role not found.</p>
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <CatalogFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        entityLabel="service role"
        entity={
          role
            ? {
                id: role.id,
                name: role.name,
                code: role.shortCode,
                description: role.description,
                kind: role.internal,
              }
            : null
        }
        codeField={{ label: 'Short code', placeholder: 'CFO' }}
        kindField={{
          label: 'Kind',
          trueLabel: 'Internal',
          falseLabel: 'Customer',
          hint: 'Internal roles are for back-office / admin work — not surfaced on customer-facing reports.',
        }}
        serverToFormField={{ shortCode: 'code', internal: 'kind' }}
        submit={(body) => {
          if (!role) throw new Error('Service role not loaded');
          if (!body.code) {
            throw new Error('code missing — CatalogFormSheet contract violation');
          }
          const patch: {
            name: string;
            shortCode: string;
            description: string | null;
            internal?: boolean;
          } = {
            name: body.name,
            shortCode: body.code,
            description: body.description,
          };
          if (body.kind !== undefined) patch.internal = body.kind;
          return serviceRolesApi.patch(role.id, patch);
        }}
        invalidateKeys={[
          queryKeys.serviceRoles.all,
          queryKeys.audit.all,
          queryKeys.serviceRoles.detail(id),
        ]}
      />
    </PageShell>
  );
}

function DetailsTable({ role }: { role: ServiceRole }) {
  const rows: DetailListRow[] = [
    {
      label: 'Short code',
      value: <code className="font-mono text-sm">{role.shortCode}</code>,
    },
    { label: 'Name', value: role.name },
    { label: 'Description', value: role.description ?? <Muted>—</Muted> },
    { label: 'Kind', value: <KindBadge internal={role.internal} /> },
    { label: 'Status', value: <StatusBadge active={role.active} /> },
    { label: 'Created', value: new Date(role.createdAt).toLocaleString() },
    { label: 'Updated', value: new Date(role.updatedAt).toLocaleString() },
  ];
  return <DetailList rows={rows} />;
}

// Number of rows DetailsTable renders — keeps the loading skeleton the same
// height as the loaded card so the layout doesn't jump.
const DETAIL_ROW_COUNT = 7;

function StatusBadge({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Inactive
    </span>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}
